// ---------------------------------------------------------------------------
// The lead step's rules (R17b)
//
// How imported leads merge with those already there, what a HubSpot list
// brings, what the ICP interpretation writes into the sourcing form, which
// channels the leads can be reached on, and the rows of the CSV export. Taken
// out of wizard/wiz-leads.ts unchanged, so the step's rebuilds (R17e–h) redo
// only the markup. Plain TypeScript.
//
// The search itself is built by src/sourcing-search.ts, the one builder the
// launch uses too (R17a); its glue in the step (searchFor, the page sizes,
// "Load 25 more") stays in wiz-leads.ts, where R17a's guards read it.
// ---------------------------------------------------------------------------

/** A lead of the draft, imported or sourced. */
export interface LeadRecord {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  linkedinUrl?: string;
  industry?: string;
  /** Real employee count of the verified company (Apollo org-scoped sourcing). */
  employeeCount?: number;
  apolloPersonId?: string;
  hasEmail?: boolean;
  hasDirectPhone?: string;
  /** Pre-existing HubSpot contact id when imported from a HubSpot list. */
  crmContactId?: string;
  /** 'csv' | 'hubspot' — origin of this lead row, surfaced in the preview. */
  source?: 'csv' | 'hubspot' | 'apollo' | 'eigen';
}

/** The sourcing form, as the draft persists it (apolloSearchInput) and the launch reads it. */
export interface SourcingParams {
  icpDescription: string;
  companyLocation: string[];
  companyIndustry: string[];
  industryKeywords: string;
  employeeCount: string[];
  revenueMin: number | null;
  revenueMax: number | null;
  jobTitle: string;
  department: string[];
  personLocation: string[];
  /**
   * Companies to source INSIDE — typically classified out of an uploaded file.
   * Not an ICP-interpreted field: it is input the operator supplied, so
   * "Re-interpret ICP" deliberately leaves it alone (see icp-reinterpret.ts).
   * Empty means the search says nothing about the company, never "match none".
   */
  organizationNames: string[];
  leadLimit: string;
}

/**
 * Merge `incoming` into `existing`, deduplicating by email when present,
 * otherwise by LinkedIn URL, so a LinkedIn-only lead (no email) is kept
 * instead of being dropped; leads with neither are not contactable and are
 * skipped. Incoming entries override existing ones (re-importing a HubSpot
 * list refreshes its rows; a CSV row for a HubSpot-imported email upgrades the
 * row but keeps the existing `crmContactId` and `source` when the new row
 * lacks one).
 */
export function mergeLeads(
  existing: LeadRecord[],
  incoming: LeadRecord[],
): { merged: LeadRecord[]; added: number; updated: number } {
  const dedupKey = (l: LeadRecord): string | null => {
    const email = l.email?.trim().toLowerCase();
    if (email) return `email:${email}`;
    const li = l.linkedinUrl?.trim().toLowerCase();
    if (li) return `li:${li}`;
    return null;
  };
  const byKey = new Map<string, LeadRecord>();
  for (const l of existing) {
    const key = dedupKey(l);
    if (key) byKey.set(key, l);
  }
  let added = 0;
  let updated = 0;
  for (const inc of incoming) {
    const key = dedupKey(inc);
    if (!key) continue;
    const prev = byKey.get(key);
    if (prev) {
      byKey.set(key, {
        ...prev,
        ...inc,
        crmContactId: inc.crmContactId ?? prev.crmContactId,
        source: inc.source ?? prev.source,
      });
      updated++;
    } else {
      byKey.set(key, inc);
      added++;
    }
  }
  return { merged: Array.from(byKey.values()), added, updated };
}

/** A HubSpot list's contacts as leads: only those with an address. */
// biome-ignore lint/suspicious/noExplicitAny: the contacts as GET /api/crm/hubspot/lists/:id/contacts sends them
export const hubspotLeads = (contacts: any[]): LeadRecord[] =>
  contacts
    .filter(c => c.email)
    .map(c => ({
      firstName: c.firstName ?? '',
      lastName: c.lastName ?? '',
      email: c.email,
      company: c.company ?? '',
      jobTitle: c.jobTitle ?? '',
      linkedinUrl: c.linkedinUrl,
      industry: c.industry,
      crmContactId: c.crmContactId,
      source: 'hubspot' as const,
    }));

/** The line over the leads after an import: what came from where, how many new and updated. */
export const importSummary = (count: number, from: string, added: number, updated: number): string =>
  `Imported ${count} from ${from} (↗ ${added} new, ↻ ${updated} updated)`;

/** The import's file label: the file, or "<n> existing + <file>" when leads were there before. */
export const importLabel = (existingCount: number, name: string): string =>
  existingCount === 0 ? name : `${existingCount} existing + ${name}`;

/** Which reader a lead file needs, by its name: a spreadsheet, text with separators, or none we read. */
export function leadFileKind(fileName: string): 'excel' | 'csv' | null {
  const name = fileName.toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel';
  if (name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt')) return 'csv';
  return null;
}

/** A lead of the sourcing preview, from the search's answer. */
// biome-ignore lint/suspicious/noExplicitAny: a lead as POST /api/apollo/search answers it
export const previewLead = (lead: any): LeadRecord => ({
  firstName: lead.firstName,
  lastName: lead.lastName,
  email: lead.email,
  company: lead.company,
  jobTitle: lead.jobTitle,
  linkedinUrl: lead.linkedinUrl,
  industry: lead.industry,
  employeeCount: lead.employeeCount,
  apolloPersonId: lead.apolloPersonId,
  hasEmail: lead.hasEmail,
  hasDirectPhone: lead.hasDirectPhone,
  source: 'eigen',
});

/** The figures over the imported leads, and what the channel warning reads. */
export interface LeadFigures {
  total: number;
  companies: number;
  withLinkedin: number;
  withEmail: number;
  /** Leads with both an address and a LinkedIn URL. */
  bothChannels: number;
  /** Leads with neither. */
  noChannel: number;
}

export const leadFigures = (leads: LeadRecord[]): LeadFigures => ({
  total: leads.length,
  companies: new Set(leads.map(l => l.company)).size,
  withLinkedin: leads.filter(l => l.linkedinUrl).length,
  withEmail: leads.filter(l => l.email).length,
  bothChannels: leads.filter(l => l.email && l.linkedinUrl).length,
  noChannel: leads.filter(l => !l.email && !l.linkedinUrl).length,
});

/**
 * The channel warning for the campaign's type. The backend skips ineligible
 * leads silently during message generation; the step says so before LLM calls
 * that produce nothing are paid for. LinkedIn-only: the leads without a URL;
 * email-only: those without an address; cross-channel: the mix, unless every
 * lead has both.
 */
export type ChannelCoverage =
  | { kind: 'linkedin'; missing: number; total: number }
  | { kind: 'email'; missing: number; total: number }
  | { kind: 'multi'; both: number; emailOnly: number; linkedinOnly: number; none: number };

export function channelCoverage(
  campaignType: 'email' | 'linkedin' | 'multi' | null | undefined,
  figures: LeadFigures,
): ChannelCoverage | null {
  if (!campaignType || figures.total === 0) return null;
  const total = figures.total;
  if (campaignType === 'linkedin') {
    const missing = total - figures.withLinkedin;
    return missing === 0 ? null : { kind: 'linkedin', missing, total };
  }
  if (campaignType === 'email') {
    const missing = total - figures.withEmail;
    return missing === 0 ? null : { kind: 'email', missing, total };
  }
  if (figures.withLinkedin === total && figures.withEmail === total) return null;
  return {
    kind: 'multi',
    both: figures.bothChannels,
    emailOnly: figures.withEmail - figures.bothChannels,
    linkedinOnly: figures.withLinkedin - figures.bothChannels,
    none: figures.noChannel,
  };
}

/** What the model read from the ICP (appliedFilters of the search's answer), read defensively. */
export interface Interpretation {
  personTitles: string[];
  personSeniorities: string[];
  personLocations: string[];
  organizationLocations: string[];
  industries: string[];
  employeeRanges: string[];
  departments: string[];
  revenueMinUsd: number | null;
  revenueMaxUsd: number | null;
  mappingFailed: boolean;
}

/** The interpretation in a search's answer, or null without one. A field that cannot be read reads as empty. */
// biome-ignore lint/suspicious/noExplicitAny: appliedFilters as the server sends it
export function readInterpretation(read: () => any): Interpretation | null {
  const af = (() => {
    try {
      return read();
    } catch {
      return undefined;
    }
  })();
  if (!af) return null;
  const list = (get: () => string[] | undefined): string[] => {
    try {
      return get() ?? [];
    } catch {
      return [];
    }
  };
  const number = (get: () => number | undefined): number | null => {
    try {
      const v = get();
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    } catch {
      return null;
    }
  };
  const flag = (get: () => boolean | undefined): boolean => {
    try {
      return get() === true;
    } catch {
      return false;
    }
  };
  return {
    personTitles: list(() => af.personTitles),
    personSeniorities: list(() => af.personSeniorities),
    personLocations: list(() => af.personLocations),
    organizationLocations: list(() => af.organizationLocations),
    industries: list(() => af.organizationIndustryKeywords),
    employeeRanges: list(() => af.employeeRanges),
    departments: list(() => af.departments),
    revenueMinUsd: number(() => af.revenueMinUsd),
    revenueMaxUsd: number(() => af.revenueMaxUsd),
    mappingFailed: flag(() => af.mappingFailed),
  };
}

/** The form's own spellings: a place or an industry, looked up in lower case. */
export interface CanonicalValues {
  countries: ReadonlyMap<string, string>;
  industries: ReadonlyMap<string, string>;
}

/**
 * The lookups for the form's dropdowns: every country by its lower-case name,
 * with the common shorthands and bare names for the entries the list writes
 * with an abbreviation, and every industry of the list.
 */
export function canonicalValues(countryNames: readonly string[], industryNames: readonly string[]): CanonicalValues {
  const countries = new Map(countryNames.map(c => [c.toLowerCase(), c]));
  countries.set('united kingdom', 'United Kingdom (UK)');
  countries.set('uk', 'United Kingdom (UK)');
  countries.set('great britain', 'United Kingdom (UK)');
  countries.set('united states', 'United States (USA)');
  countries.set('united states of america', 'United States (USA)');
  countries.set('usa', 'United States (USA)');
  return { countries, industries: new Map(industryNames.map(i => [i.toLowerCase(), i])) };
}

/**
 * Map a place string to the value we put into the location fields. Country/
 * region names are canonicalized to their dropdown entry; city-level places
 * ("Dublin, Ireland") are kept VERBATIM — Apollo accepts city strings, and
 * resolving them to the whole country would silently widen the search
 * (tester: "near Dublin" must not become all of Ireland).
 */
export const placeToFieldValue = (raw: string, canonical: CanonicalValues): string =>
  canonical.countries.get(raw.trim().toLowerCase()) ?? raw.trim();

type ListField = 'companyLocation' | 'personLocation' | 'companyIndustry' | 'department' | 'employeeCount';
type TextField = 'jobTitle' | 'industryKeywords';
type NumberField = 'revenueMin' | 'revenueMax';

/**
 * Writes an ICP interpretation into the structured form fields. A field is
 * (re)filled whenever it's empty OR still holds exactly what a PRIOR
 * interpretation auto-filled it with; the moment the user edits a field by
 * hand it stops tracking further interpretations, so their edit sticks — even
 * across a later ICP rewrite. When the NEW interpretation has no value for a
 * field, a value we auto-filled earlier is stale and is cleared; hand-edited
 * values stay. `autoFilled` is the snapshot of what the interpretation last
 * wrote per field; it is returned updated with the form.
 */
export function applyInterpretation(
  params: SourcingParams,
  autoFilled: Partial<SourcingParams>,
  af: Interpretation,
  canonical: CanonicalValues,
): { next: SourcingParams; nextAuto: Partial<SourcingParams>; changed: boolean } {
  const next: SourcingParams = { ...params };
  const nextAuto: Partial<SourcingParams> = { ...autoFilled };
  let changed = false;

  const sameArray = (a: string[] | undefined, b: string[]): boolean =>
    !!a && a.length === b.length && a.every((v, i) => v === b[i]);
  const fillArray = (key: ListField, values: string[]) => {
    const current = next[key];
    const wasAutoFilled = sameArray(autoFilled[key], current);
    if (values.length === 0) {
      if (wasAutoFilled && current.length > 0) {
        next[key] = [];
        delete nextAuto[key];
        changed = true;
      }
      return;
    }
    const untouched = current.length === 0 || wasAutoFilled;
    if (!untouched) return;
    next[key] = values;
    nextAuto[key] = values;
    changed = true;
  };
  const fillString = (key: TextField, value: string) => {
    const current = next[key];
    const wasAutoFilled = autoFilled[key] === current;
    if (!value) {
      if (wasAutoFilled && current.trim()) {
        next[key] = '';
        delete nextAuto[key];
        changed = true;
      }
      return;
    }
    const untouched = !current.trim() || wasAutoFilled;
    if (!untouched) return;
    next[key] = value;
    nextAuto[key] = value;
    changed = true;
  };
  const fillNumber = (key: NumberField, value: number | null) => {
    const current = next[key];
    const wasAutoFilled = autoFilled[key] === current;
    if (value === null) {
      if (wasAutoFilled && current !== null) {
        next[key] = null;
        delete nextAuto[key];
        changed = true;
      }
      return;
    }
    const untouched = current === null || wasAutoFilled;
    if (!untouched) return;
    next[key] = value;
    nextAuto[key] = value;
    changed = true;
  };
  const place = (raw: string) => placeToFieldValue(raw, canonical);

  fillString('jobTitle', af.personTitles.join(', '));

  const hqPlaces = [...new Set(af.organizationLocations.map(place).filter(Boolean))];
  fillArray('companyLocation', hqPlaces);

  // Person location: the mapper's person places; for company-type ICPs (person
  // places empty) the company places, so the CONTACTS are filtered to the
  // region too — without this, a Dublin company's exec sitting in Strasbourg
  // still matched. Editable/removable like any field.
  const personPlaces = [
    ...new Set(
      (af.personLocations.length > 0 ? af.personLocations : af.organizationLocations).map(place).filter(Boolean),
    ),
  ];
  fillArray('personLocation', personPlaces);

  const canonicalIndustries = [
    ...new Set(
      af.industries.map(i => canonical.industries.get(i.trim().toLowerCase())).filter((c): c is string => !!c),
    ),
  ];
  fillArray('companyIndustry', canonicalIndustries);

  const leftoverIndustries = af.industries.filter(i => !canonical.industries.has(i.trim().toLowerCase()));
  fillString('industryKeywords', leftoverIndustries.join(', '));

  // Server already canonicalizes departments against the DEPARTMENTS enum.
  fillArray('department', [...new Set(af.departments)]);

  // Employee count: the EXACT interpreted range (e.g. "100-5000"), not the
  // fixed buckets — testers read "201-500" as "the mapping starts at 201" when
  // their ICP said 100. The server parses "min-max" and "min+" like buckets.
  const employeeChips = af.employeeRanges
    .map(r => {
      const m = r.match(/^(\d+),(\d+)$/);
      if (!m) return null;
      const lo = Number(m[1]);
      const hi = Number(m[2]);
      return hi >= 1000000 ? `${lo}+` : `${lo}-${hi}`;
    })
    .filter((c): c is string => c !== null);
  fillArray('employeeCount', [...new Set(employeeChips)]);

  fillNumber('revenueMin', af.revenueMinUsd !== null ? Math.round(af.revenueMinUsd / 1_000_000) : null);
  fillNumber('revenueMax', af.revenueMaxUsd !== null ? Math.round(af.revenueMaxUsd / 1_000_000) : null);

  return { next, nextAuto, changed };
}

/** A lead of the export, after the enrichment: what the enrichment found, else what the search had. */
export interface EnrichedLead {
  firstName?: string;
  lastName?: string;
  email?: string;
  linkedinUrl?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
}

/**
 * The export's rows: the search's leads, each with what the enrichment found
 * (names and company only when not empty; address, LinkedIn and industry when
 * the enrichment had the field at all).
 */
export const exportRows = (previews: LeadRecord[], enrichedById: ReadonlyMap<string, EnrichedLead>): LeadRecord[] =>
  previews.map(lead => {
    const enriched = lead.apolloPersonId ? enrichedById.get(lead.apolloPersonId) : undefined;
    return {
      firstName: enriched?.firstName || lead.firstName,
      lastName: enriched?.lastName || lead.lastName,
      email: enriched?.email ?? lead.email,
      company: enriched?.company || lead.company,
      jobTitle: enriched?.jobTitle || lead.jobTitle,
      linkedinUrl: enriched?.linkedinUrl ?? lead.linkedinUrl,
      industry: enriched?.industry ?? lead.industry,
      employeeCount: lead.employeeCount,
    };
  });

/** The export's file name: the campaign's name made safe, "<name>_leads.csv". */
export const exportFileName = (campaignName: string): string =>
  `${(campaignName || 'leads')
    .trim()
    .replace(/[^\w-]+/g, '_')
    .toLowerCase()}_leads.csv`;
